import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { HeaderActions } from './HeaderActions'
import { Navigation } from './Navigation'
import { useTableContext } from './TableContext'
import { Container } from '@/interfaces/container'
import { useLogisticContainer } from '../hooks/useLogisticContainer'
import ContainerWizard from '../wizzardContainer/ContainerWizard'

// DOCS: https://ui.shadcn.com/docs/components/data-table#basic-table

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
}

const CONTAINER_TABLE_COLUMNS_STORAGE_KEY = 'container-table-columns'

export function DataTable<TData, TValue>({
  columns,
}: DataTableProps<TData, TValue>) {
  const navigate = useNavigate()

  // Use the table context
  const {
    currentPage,
    containerData,
    isLoading,
    skeletonRowCount,
    isFetching,
    isError,
  } = useTableContext()

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [isMobileView, setIsMobileView] = useState(false)
  const { onRowClick, showNewContainer, editContainer, onCloseWizard } =
    useLogisticContainer()

  // Create skeleton columns when loading
  const skeletonColumns = useMemo(() => {
    if (!isLoading) return columns

    return columns.map((column) => {
      // Different width based on column type
      let width = '80%'
      let height = '20px'

      return {
        ...column,
        cell: () => (
          <div className="flex items-center">
            <Skeleton className={`h-${height} w-${width}`} />
          </div>
        ),
      }
    })
  }, [columns, isLoading])

  // Create skeleton data for loading state
  const skeletonData = useMemo(() => {
    if (!isLoading) return []

    return Array.from({ length: skeletonRowCount }).map((_, index) => ({
      id: `skeleton-${index}`,
    }))
  }, [isLoading, skeletonRowCount])

  // Create a memoized version of the data to ensure stable references
  const tableData = useMemo(() => {
    if (isLoading) {
      return skeletonData as TData[]
    }
    return (containerData?.items as TData[]) || []
  }, [containerData?.items, isLoading, skeletonData])

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  // Set default column visibility based on screen size
  useEffect(() => {
    if (isMobileView) {
      // On mobile, show only high-priority columns
      const mobileVisibility: VisibilityState = {
        origin: false,
        shipping: false,
        totalItems: false,
        areaSales: false,
        deliveryAt: false,
        paidAt: false,
      }
      setColumnVisibility((prev) => ({ ...prev, ...mobileVisibility }))
    }
  }, [isMobileView])

  // Inicializar visibilidad de columnas según localStorage o lista de ocultas por defecto
  useEffect(() => {
    const storedColumns = localStorage.getItem(CONTAINER_TABLE_COLUMNS_STORAGE_KEY);
    if (storedColumns) {
      // Si hay configuración previa, úsala
      const parsedColumns = JSON.parse(storedColumns);
      if (parsedColumns && typeof parsedColumns === 'object') {
        setColumnVisibility(parsedColumns);
        return;
      }
    }
    // No hay configuración previa, usar lista explícita de ocultas por defecto
    const defaultHiddenKeys = ['updatedAt', 'ownerCompany', 'managerCompany', 'importerCompany', 'transporterCompany', 'storageCompany'];
    const initialVisibility: VisibilityState = {};
    defaultHiddenKeys.forEach((key) => {
      initialVisibility[key] = false;
    });
    setColumnVisibility(initialVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // Handle column visibility changes - both update state and save to localStorage
  const handleColumnVisibilityChange = (
    updatedVisibility:
      | VisibilityState
      | ((prev: VisibilityState) => VisibilityState)
  ) => {
    // First update the state
    setColumnVisibility(updatedVisibility)

    // Then save to localStorage
    try {
      // If it's a function, we need to get the actual value
      const newVisibility =
        typeof updatedVisibility === 'function'
          ? updatedVisibility(columnVisibility)
          : updatedVisibility

      if (Object.keys(newVisibility).length > 0) {
        localStorage.setItem(
          CONTAINER_TABLE_COLUMNS_STORAGE_KEY,
          JSON.stringify(newVisibility)
        )
      }
    } catch (error) {
      console.error('Failed to save column visibility settings:', error)
      // Continue without saving settings on error
    }
  }

  // Reset table when the page changes by recreating the table instance
  const table = useReactTable({
    data: tableData,
    columns: isLoading ? (skeletonColumns as ColumnDef<TData, any>[]) : columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    state: {
      columnVisibility,
    },
    // This key ensures the table is reset when data changes
    manualPagination: true,
  })

  const navigateToContainer = (row: any, event: React.MouseEvent) => {
    // Don't navigate if in loading state
    if (isLoading) return

    const container = row.original as Container

    event.stopPropagation()
    onRowClick(container)
  }

  // Card view for mobile rendering
  const renderMobileCard = (row: any) => {
    // For skeleton rows
    if (isLoading) {
      return (
        <div
          key={row.id}
          className="mb-4 rounded-md border bg-card p-4 shadow-sm"
          role="row"
          aria-rowindex={row.index + 2}
        >
          <div className="grid grid-cols-2 gap-2">
            {table.getVisibleLeafColumns().map((column) => (
              <div key={column.id} className="mb-2" role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id}
                </div>
                <div className="flex h-5 items-center">
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div
        key={row.id}
        className="mb-4 rounded-md border bg-card p-4 shadow-sm"
        role="row"
        aria-rowindex={row.index + 2} // +2 for header row and 0-indexing
      >
        <div className="grid grid-cols-2 gap-2">
          {row.getVisibleCells().map((cell: any) => {
            // Skip rendering empty cells
            if (cell.column.id === 'shipping' && !row.original.shipping)
              return null

            const header =
              typeof cell.column.columnDef.header === 'string'
                ? cell.column.columnDef.header
                : cell.column.id

            return (
              <div key={cell.id} className="mb-2" role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {header}
                </div>
                <div>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card">
      <HeaderActions
        columns={
          table.getAllColumns() as unknown as Column<Container, unknown>[]
        }
      />

      <div className="border-t">
        <div>
          {isLoading ||
          isFetching ||
          isError ||
          table.getRowModel().rows?.length ? (
            isMobileView ? (
              // Mobile card view
              <div className="p-4" role="table" aria-label="Tabla de facturas">
                <div className="sr-only" role="rowgroup">
                  <div role="row">
                    {table.getHeaderGroups().map((headerGroup) =>
                      headerGroup.headers.map((header) => (
                        <div key={header.id} role="columnheader">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div role="rowgroup" key={`page-${currentPage}`}>
                  {table.getRowModel().rows.map((row) => renderMobileCard(row))}
                </div>
              </div>
            ) : (
              // Standard table view
              <div
                className="overflow-x-auto"
                role="region"
                aria-label="Tabla de facturas"
              >
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return (
                            <TableHead
                              key={header.id}
                              className="px-4 text-left"
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </TableHead>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody key={`page-${currentPage}`}>
                    {table.getRowModel().rows.map((row) => {
                      // For skeleton rows during loading
                      if (isLoading) {
                        return (
                          <TableRow key={row.id} className="h-11 text-left">
                            {row.getVisibleCells().map((cell) => {
                              return (
                                <TableCell
                                  key={cell.id}
                                  className="px-0.5 py-1 text-left"
                                >
                                  <Skeleton className="h-10 w-full rounded-none bg-gray-100" />
                                </TableCell>
                              )
                            })}
                          </TableRow>
                        )
                      }

                      return (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                          className="hover:bg-accent/50"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className="px-4 py-1 text-left"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {isError ? 'Error al cargar los datos' : 'No hay resultados'}
              </p>
            </div>
          )}
          <Navigation />
        </div>
      </div>
      {(showNewContainer || !!editContainer) && (
        <ContainerWizard
          showState={showNewContainer}
          onClose={onCloseWizard}
          editContainer={editContainer}
        />
      )}
    </div>
  )
}
